/**
 * Resolución del nombre del responsable en movimientos de caja (lista, ticket de cierre, etc.).
 *
 * Orden de precedencia:
 * 1. `userName` persistido en el movimiento al registrar (auth + contraseña en caja).
 * 2. Catálogo de personal (`personal_empleados`) por `usuarioId`.
 * 3. Mapa derivado de ventas (mismo contexto de sesión / historial filtrado).
 * 4. Formato legible de id (`fmtEmp`).
 */

import type { MovimientoCaja, Venta } from "../types/caja.types";
import {
  fmtEmp,
  getEmpleadoNombre,
  type EmpleadoNombre,
} from "./getEmpleadoNombre";

/** Primer `userName` visto por `usuarioId` en ventas no anuladas (último recurso). */
export function buildVendedorNombrePorIdFromVentas(
  ventas: Venta[],
): Map<string, string> {
  const m = new Map<string, string>();
  for (const v of ventas) {
    if (v.estado === "anulada") continue;
    const uid = String(v.usuarioId ?? "").trim();
    const uname = String(v.userName ?? "").trim();
    if (!uid || !uname) continue;
    if (!m.has(uid)) m.set(uid, uname);
  }
  return m;
}

export function nombreResponsableMovimientoCaja(
  mov: Pick<MovimientoCaja, "userName" | "usuarioId">,
  opts: {
    empleadosCatalog?: EmpleadoNombre[];
    nombrePorUsuarioIdFromVentas: Map<string, string>;
  },
): string {
  const uname = mov.userName?.trim();
  if (uname && !/^\d+$/.test(uname)) return fmtEmp(uname);

  const uid = mov.usuarioId?.trim();
  if (uid && opts.empleadosCatalog?.length) {
    const fromCat = getEmpleadoNombre(
      uid,
      opts.empleadosCatalog,
      uname || undefined,
    );
    if (fromCat !== "Desconocido") return fmtEmp(fromCat);
  }

  if (uid) {
    const fromVentas = opts.nombrePorUsuarioIdFromVentas.get(uid);
    if (fromVentas?.trim()) return fmtEmp(fromVentas);
  }

  return fmtEmp(mov.userName || mov.usuarioId);
}
